import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Skeleton,
  Stack,
  useColorModeValue,
  HStack,
  Progress,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  FiAlertTriangle,
  FiAward,
  FiBarChart,
  FiBookOpen,
  FiClock,
  FiDownload,
  FiRefreshCw,
  FiSearch,
  FiTarget,
  FiUsers,
} from 'react-icons/fi';

import { AdminLayout, Breadcrumb, Card, DataTable } from '@grcs/components';
import { TableColumn } from '@grcs/components/types';

import { useAnalyticsService } from '@grcs/hook/useAnalyticsService';
import {
  useClassOperations,
  useStudentOperations,
} from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import {
  ClassProfile,
  StudentProfile,
  StudentWithAnalytics,
} from '@grcs/types/DataTypes';

interface EnhancedStudentAnalytics extends StudentWithAnalytics {
  performance: 'excellent' | 'good' | 'average' | 'struggling';
  engagement: 'high' | 'medium' | 'low';
  riskLevel: 'none' | 'low' | 'medium' | 'high';
  classInfo?: ClassProfile;
  completionRate: number;
  averageScore: number;
}

interface FilterOptions {
  search: string;
  class: string;
  performance: 'all' | 'excellent' | 'good' | 'average' | 'struggling';
  engagement: 'all' | 'high' | 'medium' | 'low';
  riskLevel: 'all' | 'none' | 'low' | 'medium' | 'high';
  sortBy: 'name' | 'performance' | 'completion' | 'time' | 'risk';
  sortOrder: 'asc' | 'desc';
}

export const StudentAnalyticsTeacherPageEnhanced: React.FC = () => {
  const analyticsService = useAnalyticsService();
  const classOps = useClassOperations();
  const studentOps = useStudentOperations();
  const toast = useToast();
  const surface = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.600');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const [error, setError] = useState('');
  const [{ user_token }] = useLocalStorage('user', {});

  const [studentsWithAnalytics, setStudentsWithAnalytics] = useState<
    EnhancedStudentAnalytics[]
  >([]);
  const [classes, setClasses] = useState<ClassProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    search: '',
    class: 'all',
    performance: 'all',
    engagement: 'all',
    riskLevel: 'all',
    sortBy: 'performance',
    sortOrder: 'desc',
  });

  useEffect(() => {
    fetchStudentAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_token]);

  const fetchStudentAnalytics = async (refresh = false) => {
    if (!user_token) {
      setIsLoading(false);
      return;
    }
    try {
      setError('');
      if (refresh) setIsRefreshing(true);
      else setIsLoading(true);

      // Get teacher's classes
      const { data: teacherClasses } = await classOps.getClassesByTeacherId(
        user_token,
      );
      setClasses(teacherClasses || []);

      // Collect all students from teacher's classes
      let allStudents: StudentProfile[] = [];
      for (const classItem of teacherClasses || []) {
        const { data: classStudents } =
          await studentOps.getAllStudentsByClassId(classItem.class_token);
        const studentsWithClass = (classStudents || []).map((student) => ({
          ...student,
          classInfo: classItem,
        }));
        allStudents = [...allStudents, ...studentsWithClass];
      }

      if (allStudents.length === 0) {
        setStudentsWithAnalytics([]);
        return;
      }

      // Fetch analytics for all students
      const analyticsData = await fetchStudentsWithAnalytics(
        allStudents,
        analyticsService,
      );

      // Enhance with additional metrics
      const enhancedStudents: EnhancedStudentAnalytics[] = analyticsData.map(
        (student) => {
          // Calculate performance metrics
          const completionRate = Math.round(student.percent_completed || 0);
          const averageScore = Math.round(student.average_score || 0);
          const avgTime = student.average_time || 0;

          // Determine performance level
          let performance: EnhancedStudentAnalytics['performance'] =
            'struggling';
          if (averageScore >= 90) performance = 'excellent';
          else if (averageScore >= 80) performance = 'good';
          else if (averageScore >= 70) performance = 'average';

          // Determine engagement level based on completion rate and time
          let engagement: EnhancedStudentAnalytics['engagement'] = 'low';
          if (completionRate >= 80 && avgTime > 0) engagement = 'high';
          else if (completionRate >= 60) engagement = 'medium';

          // Determine risk level
          let riskLevel: EnhancedStudentAnalytics['riskLevel'] = 'none';
          if (completionRate < 50 || averageScore < 60) riskLevel = 'high';
          else if (completionRate < 70 || averageScore < 75)
            riskLevel = 'medium';
          else if (completionRate < 85) riskLevel = 'low';

          return {
            ...student,
            performance,
            engagement,
            riskLevel,
            completionRate,
            averageScore,
          };
        },
      );

      setStudentsWithAnalytics(enhancedStudents);
    } catch (error) {
      console.error('Error fetching student analytics:', error);
      setError(
        'Student analytics could not be loaded. Try refreshing the page.',
      );
      toast({
        title: 'Error loading student analytics',
        description: 'Failed to load student performance data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Filter and sort students
  const filteredAndSortedStudents = useMemo(() => {
    let filtered = studentsWithAnalytics.filter((student) => {
      // Search filter
      if (filters.search.trim()) {
        const searchLower = filters.search.trim().toLowerCase();
        if (
          !`${student.first_name || ''} ${student.last_name || ''} ${
            student.email || ''
          }`
            .toLowerCase()
            .includes(searchLower)
        )
          return false;
      }

      // Class filter
      if (filters.class !== 'all') {
        const studentClass = classes.find(
          (c) => c.class_token === student.classInfo?.class_token,
        );
        if (!studentClass || studentClass.class_token !== filters.class)
          return false;
      }

      // Performance filter
      if (
        filters.performance !== 'all' &&
        student.performance !== filters.performance
      ) {
        return false;
      }

      // Engagement filter
      if (
        filters.engagement !== 'all' &&
        student.engagement !== filters.engagement
      ) {
        return false;
      }

      // Risk level filter
      if (
        filters.riskLevel !== 'all' &&
        student.riskLevel !== filters.riskLevel
      ) {
        return false;
      }

      return true;
    });

    // Sort students
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (filters.sortBy) {
        case 'name':
          aValue = `${a.first_name} ${a.last_name}`;
          bValue = `${b.first_name} ${b.last_name}`;
          break;
        case 'performance':
          const performanceOrder = {
            excellent: 4,
            good: 3,
            average: 2,
            struggling: 1,
          };
          aValue = performanceOrder[a.performance];
          bValue = performanceOrder[b.performance];
          break;
        case 'completion':
          aValue = a.completionRate;
          bValue = b.completionRate;
          break;
        case 'time':
          aValue = a.average_time || 0;
          bValue = b.average_time || 0;
          break;
        case 'risk':
          const riskOrder = { high: 4, medium: 3, low: 2, none: 1 };
          aValue = riskOrder[a.riskLevel];
          bValue = riskOrder[b.riskLevel];
          break;
        default:
          return 0;
      }

      if (aValue === bValue) return 0;
      if (filters.sortOrder === 'desc') {
        return bValue > aValue ? 1 : -1;
      }
      return aValue > bValue ? 1 : -1;
    });

    return filtered;
  }, [studentsWithAnalytics, classes, filters]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (studentsWithAnalytics.length === 0) {
      return {
        total: 0,
        atRisk: 0,
        highPerformers: 0,
        avgCompletion: 0,
        avgScore: 0,
      };
    }

    const total = studentsWithAnalytics.length;
    const atRisk = studentsWithAnalytics.filter(
      (s) => s.riskLevel === 'high' || s.riskLevel === 'medium',
    ).length;
    const highPerformers = studentsWithAnalytics.filter(
      (s) => s.performance === 'excellent' || s.performance === 'good',
    ).length;
    const avgCompletion = Math.round(
      studentsWithAnalytics.reduce((sum, s) => sum + s.completionRate, 0) /
        total,
    );
    const avgScore = Math.round(
      studentsWithAnalytics.reduce((sum, s) => sum + s.averageScore, 0) / total,
    );

    return { total, atRisk, highPerformers, avgCompletion, avgScore };
  }, [studentsWithAnalytics]);

  const handleExportData = () => {
    const csvContent = [
      'Name,Email,Class,Performance,Completion Rate,Average Score,Engagement,Risk Level',
      ...filteredAndSortedStudents.map(
        (s) =>
          `"${s.first_name} ${s.last_name}","${s.email}","${
            s.classInfo?.class_name || 'Unknown'
          }","${s.performance}","${s.completionRate}%","${s.averageScore}%","${
            s.engagement
          }","${s.riskLevel}"`,
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_analytics.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: TableColumn<EnhancedStudentAnalytics>[] = [
    {
      key: 'student',
      header: 'Student',
      render: (student) => (
        <HStack spacing={3}>
          <Avatar
            size="sm"
            name={`${student.first_name} ${student.last_name}`}
            bg="blue.500"
          />
          <VStack align="start" spacing={0}>
            <Text fontWeight="medium">
              {`${student.first_name} ${student.last_name}`}
            </Text>
            <Text fontSize="sm" color={muted}>
              {student.email}
            </Text>
            <Badge size="xs" variant="subtle">
              {student.classInfo?.class_name || 'Unknown Class'}
            </Badge>
          </VStack>
        </HStack>
      ),
    },
    {
      key: 'performance',
      header: 'Performance',
      render: (student) => (
        <VStack align="start" spacing={2}>
          <HStack spacing={2}>
            <Badge
              colorScheme={
                student.performance === 'excellent'
                  ? 'green'
                  : student.performance === 'good'
                  ? 'blue'
                  : student.performance === 'average'
                  ? 'yellow'
                  : 'red'
              }
              variant="subtle"
            >
              {student.performance}
            </Badge>
          </HStack>
          <VStack align="start" spacing={1}>
            <HStack justify="space-between" width="120px">
              <Text fontSize="sm">Score:</Text>
              <Text fontSize="sm" fontWeight="medium">
                {student.averageScore}%
              </Text>
            </HStack>
            <Progress
              value={student.averageScore}
              size="sm"
              width="120px"
              colorScheme={
                student.averageScore >= 90
                  ? 'green'
                  : student.averageScore >= 80
                  ? 'blue'
                  : student.averageScore >= 70
                  ? 'yellow'
                  : 'red'
              }
            />
          </VStack>
        </VStack>
      ),
    },
    {
      key: 'engagement',
      header: 'Engagement',
      render: (student) => (
        <VStack align="start" spacing={2}>
          <HStack spacing={2}>
            <Badge
              colorScheme={
                student.engagement === 'high'
                  ? 'green'
                  : student.engagement === 'medium'
                  ? 'blue'
                  : 'orange'
              }
              variant="subtle"
            >
              {student.engagement}
            </Badge>
            {student.riskLevel !== 'none' && (
              <Badge
                colorScheme={student.riskLevel === 'high' ? 'red' : 'orange'}
                variant="subtle"
              >
                {student.riskLevel} risk
              </Badge>
            )}
          </HStack>
          <VStack align="start" spacing={1}>
            <HStack justify="space-between" width="120px">
              <Text fontSize="sm">Completion:</Text>
              <Text fontSize="sm" fontWeight="medium">
                {student.completionRate}%
              </Text>
            </HStack>
            <Progress
              value={student.completionRate}
              size="sm"
              width="120px"
              colorScheme={
                student.completionRate >= 80
                  ? 'green'
                  : student.completionRate >= 60
                  ? 'blue'
                  : 'orange'
              }
            />
          </VStack>
        </VStack>
      ),
    },
    {
      key: 'activity',
      header: 'Activity',
      render: (student) => (
        <VStack align="start" spacing={1}>
          <HStack spacing={1}>
            <FiClock />
            <Text fontSize="sm">
              {Math.round((student.average_time || 0) / 60000) || 0} min avg
            </Text>
          </HStack>
          <HStack spacing={1}>
            <FiBookOpen />
            <Text fontSize="sm">{student.completed || 0} completed</Text>
          </HStack>
          <Text fontSize="xs" color={muted}>
            {student.total_students || 0} total assignments
          </Text>
        </VStack>
      ),
    },
  ];

  const hasFilters = Boolean(
    filters.search ||
      filters.class !== 'all' ||
      filters.performance !== 'all' ||
      filters.engagement !== 'all' ||
      filters.riskLevel !== 'all',
  );
  const clearFilters = () =>
    setFilters((prev) => ({
      ...prev,
      search: '',
      class: 'all',
      performance: 'all',
      engagement: 'all',
      riskLevel: 'all',
    }));
  const topStudents = filteredAndSortedStudents
    .filter((student) => student.averageScore >= 80)
    .sort((a, b) => b.averageScore - a.averageScore)
    .slice(0, 5);
  const supportStudents = filteredAndSortedStudents
    .filter(
      (student) =>
        student.riskLevel === 'high' || student.performance === 'struggling',
    )
    .sort((a, b) => a.averageScore - b.averageScore)
    .slice(0, 5);
  const visibleClasses = classes.filter((classItem) =>
    hasFilters
      ? filteredAndSortedStudents.some(
          (student) => student.classInfo?.class_token === classItem.class_token,
        )
      : true,
  );

  return (
    <AdminLayout padding={3}>
      <VStack spacing={4} align="stretch">
        <Box>
          <Breadcrumb
            items={[
              { label: 'Teacher', href: '/teacher' },
              { label: 'Student Analytics', isCurrent: true },
            ]}
          />
          <Flex
            justify="space-between"
            align="center"
            gap={3}
            wrap="wrap"
            mt={3}
          >
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiRefreshCw />}
                isLoading={isRefreshing}
                isDisabled={isLoading}
                onClick={() => fetchStudentAnalytics(true)}
              >
                Refresh
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FiDownload />}
                isDisabled={
                  isLoading ||
                  isRefreshing ||
                  filteredAndSortedStudents.length === 0
                }
                onClick={handleExportData}
              >
                Export CSV
              </Button>
            </HStack>
          </Flex>
        </Box>
        {error && (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Text fontSize="sm">{error}</Text>
          </Alert>
        )}
        <Box>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={3}>
            {[
              { label: 'Students', value: summaryStats.total, icon: FiUsers },
              {
                label: 'At risk',
                value: summaryStats.atRisk,
                icon: FiAlertTriangle,
              },
              {
                label: 'Top students',
                value: summaryStats.highPerformers,
                icon: FiAward,
              },
              {
                label: 'Avg. completion',
                value: `${summaryStats.avgCompletion}%`,
                icon: FiTarget,
              },
              {
                label: 'Avg. score',
                value: `${summaryStats.avgScore}%`,
                icon: FiBarChart,
              },
            ].map(({ label, value, icon: Icon }) => (
              <Box
                key={label}
                p={4}
                bg={surface}
                borderWidth="1px"
                borderColor={border}
                borderRadius="lg"
              >
                <HStack justify="space-between" color={muted} mb={2}>
                  <Text fontSize="sm">{label}</Text>
                  <Icon aria-hidden="true" />
                </HStack>
                <Skeleton isLoaded={!isLoading}>
                  <Text fontSize="2xl" fontWeight="bold" lineHeight="short">
                    {value}
                  </Text>
                </Skeleton>
              </Box>
            ))}
          </SimpleGrid>
        </Box>

        <Box
          bg={surface}
          borderWidth="1px"
          borderColor={border}
          borderRadius="lg"
          p={3}
        >
          <Stack direction={{ base: 'column', md: 'row' }} spacing={3}>
            <InputGroup size="sm" flex={1}>
              <InputLeftElement pointerEvents="none">
                <FiSearch />
              </InputLeftElement>
              <Input
                aria-label="Search students"
                placeholder="Search name or email"
                value={filters.search}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    search: event.target.value,
                  }))
                }
                borderRadius="md"
              />
            </InputGroup>
            <Select
              size="sm"
              aria-label="Filter by class"
              value={filters.class}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, class: event.target.value }))
              }
              width={{ base: 'full', md: '220px' }}
              borderRadius="md"
            >
              <option value="all">All classes</option>
              {classes.map((classItem) => (
                <option
                  key={classItem.class_token}
                  value={classItem.class_token}
                >
                  {classItem.class_name}
                </option>
              ))}
            </Select>
          </Stack>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 5 }} spacing={2} mt={3}>
            {[
              {
                key: 'performance' as const,
                label: 'Performance',
                options: [
                  ['all', 'All performance levels'],
                  ['excellent', 'Excellent'],
                  ['good', 'Good'],
                  ['average', 'Average'],
                  ['struggling', 'Struggling'],
                ],
              },
              {
                key: 'engagement' as const,
                label: 'Engagement',
                options: [
                  ['all', 'All engagement levels'],
                  ['high', 'High engagement'],
                  ['medium', 'Medium engagement'],
                  ['low', 'Low engagement'],
                ],
              },
              {
                key: 'riskLevel' as const,
                label: 'Risk level',
                options: [
                  ['all', 'All risk levels'],
                  ['high', 'High risk'],
                  ['medium', 'Medium risk'],
                  ['low', 'Low risk'],
                  ['none', 'No risk'],
                ],
              },
              {
                key: 'sortBy' as const,
                label: 'Sort by',
                options: [
                  ['performance', 'Sort: performance'],
                  ['name', 'Sort: name'],
                  ['completion', 'Sort: completion'],
                  ['time', 'Sort: average time'],
                  ['risk', 'Sort: risk'],
                ],
              },
              {
                key: 'sortOrder' as const,
                label: 'Sort order',
                options: [
                  ['desc', 'Descending'],
                  ['asc', 'Ascending'],
                ],
              },
            ].map(({ key, label, options }) => (
              <Select
                key={key}
                size="sm"
                borderRadius="md"
                aria-label={label}
                value={filters[key]}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, [key]: event.target.value }))
                }
              >
                {options.map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </Select>
            ))}
          </SimpleGrid>
          <Flex
            justify="space-between"
            align="center"
            gap={2}
            wrap="wrap"
            mt={2}
          >
            <Text fontSize="xs" color={muted} role="status">
              {isLoading
                ? 'Loading students…'
                : `${filteredAndSortedStudents.length} of ${studentsWithAnalytics.length} students • Filters apply to all views and CSV export`}
            </Text>
            {hasFilters && (
              <Button size="xs" variant="ghost" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </Flex>
        </Box>

        <Box
          bg={surface}
          borderWidth="1px"
          borderColor={border}
          borderRadius="lg"
          p={{ base: 3, md: 4 }}
          minW={0}
        >
          <Tabs colorScheme="blue" isLazy>
            <TabList overflowX="auto">
              {['Students', 'Performance', 'Risk', 'Classes'].map((label) => (
                <Tab key={label} fontSize="sm" fontWeight="600" flexShrink={0}>
                  {label}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              <TabPanel px={0} pt={4} pb={0}>
                <DataTable
                  compact
                  size="sm"
                  striped={false}
                  data={filteredAndSortedStudents}
                  columns={columns}
                  loading={isLoading}
                  emptyState={
                    <VStack spacing={2} py={8} color={muted} textAlign="center">
                      <FiUsers size={28} aria-hidden="true" />
                      <Text fontWeight="600">
                        {hasFilters
                          ? 'No matching students'
                          : 'No students yet'}
                      </Text>
                      <Text fontSize="sm">
                        {hasFilters
                          ? 'Try another search or clear your filters.'
                          : 'Analytics will appear when students are enrolled in your classes.'}
                      </Text>
                      {hasFilters && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={clearFilters}
                        >
                          Clear filters
                        </Button>
                      )}
                    </VStack>
                  }
                />
              </TabPanel>

              {/* Performance Insights Tab */}
              <TabPanel px={0} pt={4} pb={0}>
                <Skeleton isLoaded={!isLoading}>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                    <Card variant="outline" padding={4}>
                      <VStack align="stretch" spacing={4}>
                        <Text fontWeight="semibold" fontSize="md">
                          Top students · score ≥80%
                        </Text>
                        {topStudents.length === 0 && (
                          <Text fontSize="sm" color={muted}>
                            No top students in this selection.
                          </Text>
                        )}
                        {topStudents.map((student, index) => (
                          <HStack
                            key={student.student_token}
                            justify="space-between"
                          >
                            <HStack>
                              <Badge colorScheme="yellow" variant="subtle">
                                #{index + 1}
                              </Badge>
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="medium" fontSize="sm">
                                  {`${student.first_name} ${student.last_name}`}
                                </Text>
                                <Text fontSize="xs" color={muted}>
                                  {student.classInfo?.class_name}
                                </Text>
                              </VStack>
                            </HStack>
                            <Text fontWeight="bold" color="green.500">
                              {student.averageScore}%
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Card>

                    <Card variant="outline" padding={4}>
                      <VStack align="stretch" spacing={4}>
                        <Text fontWeight="semibold" fontSize="md">
                          Students needing support
                        </Text>
                        {supportStudents.length === 0 && (
                          <Text fontSize="sm" color={muted}>
                            No students flagged for support in this selection.
                          </Text>
                        )}
                        {supportStudents.map((student) => (
                          <HStack
                            key={student.student_token}
                            justify="space-between"
                          >
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="medium" fontSize="sm">
                                {`${student.first_name} ${student.last_name}`}
                              </Text>
                              <HStack spacing={2}>
                                <Text fontSize="xs" color={muted}>
                                  {student.completionRate}% completion
                                </Text>
                                <Badge colorScheme="red" size="xs">
                                  {student.riskLevel} risk
                                </Badge>
                              </HStack>
                            </VStack>
                            <Text fontWeight="bold" color="red.500">
                              {student.averageScore}%
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Card>
                  </SimpleGrid>
                </Skeleton>
              </TabPanel>

              {/* Risk Assessment Tab */}
              <TabPanel px={0} pt={4} pb={0}>
                <Skeleton isLoaded={!isLoading}>
                  <VStack spacing={4} align="stretch">
                    <Text fontSize="md" fontWeight="semibold">
                      Risk Distribution
                    </Text>

                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                      {(['high', 'medium', 'low', 'none'] as const).map(
                        (level) => {
                          const count = filteredAndSortedStudents.filter(
                            (student) => student.riskLevel === level,
                          ).length;
                          const percentage = filteredAndSortedStudents.length
                            ? Math.round(
                                (count / filteredAndSortedStudents.length) *
                                  100,
                              )
                            : 0;
                          return (
                            <Card key={level} variant="outline" padding={4}>
                              <Badge
                                colorScheme={
                                  level === 'high'
                                    ? 'red'
                                    : level === 'medium'
                                    ? 'orange'
                                    : level === 'low'
                                    ? 'blue'
                                    : 'green'
                                }
                              >
                                {level === 'none' ? 'No' : level} risk
                              </Badge>
                              <Text fontSize="2xl" fontWeight="bold" mt={2}>
                                {count}
                              </Text>
                              <Text fontSize="xs" color={muted} mb={2}>
                                {percentage}% of selected students
                              </Text>
                              <Progress
                                value={percentage}
                                size="xs"
                                colorScheme="blue"
                                borderRadius="full"
                                aria-label={`${level} risk proportion`}
                              />
                            </Card>
                          );
                        },
                      )}
                    </SimpleGrid>

                    <Alert
                      status="info"
                      borderRadius="lg"
                      alignItems="flex-start"
                    >
                      <AlertIcon />
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="semibold">
                          Risk Assessment Criteria:
                        </Text>
                        <Text fontSize="sm">
                          • High Risk: &lt;50% completion rate OR &lt;60%
                          average score
                        </Text>
                        <Text fontSize="sm">
                          • Medium Risk: &lt;70% completion rate OR &lt;75%
                          average score
                        </Text>
                        <Text fontSize="sm">
                          • Low Risk: &lt;85% completion rate
                        </Text>
                        <Text fontSize="sm">
                          • No Risk: ≥85% completion rate AND ≥75% average score
                        </Text>
                      </VStack>
                    </Alert>
                  </VStack>
                </Skeleton>
              </TabPanel>

              {/* Class Comparison Tab */}
              <TabPanel px={0} pt={4} pb={0}>
                <Skeleton isLoaded={!isLoading}>
                  <VStack spacing={4} align="stretch">
                    <Text fontSize="md" fontWeight="semibold">
                      Class Performance
                    </Text>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                      {visibleClasses.length === 0 && (
                        <Text fontSize="sm" color={muted}>
                          No classes to display for this selection.
                        </Text>
                      )}
                      {visibleClasses.map((classItem) => {
                        const classStudents = filteredAndSortedStudents.filter(
                          (s) =>
                            s.classInfo?.class_token === classItem.class_token,
                        );
                        const avgScore =
                          Math.round(
                            classStudents.reduce(
                              (sum, s) => sum + s.averageScore,
                              0,
                            ) / classStudents.length,
                          ) || 0;
                        const avgCompletion =
                          Math.round(
                            classStudents.reduce(
                              (sum, s) => sum + s.completionRate,
                              0,
                            ) / classStudents.length,
                          ) || 0;

                        return (
                          <Card
                            key={classItem.class_token}
                            variant="outline"
                            padding={4}
                          >
                            <VStack align="stretch" spacing={4}>
                              <HStack justify="space-between">
                                <Text fontWeight="semibold">
                                  {classItem.class_name}
                                </Text>
                                <Badge variant="subtle">
                                  {classStudents.length} students
                                </Badge>
                              </HStack>

                              <SimpleGrid columns={2} spacing={4}>
                                <VStack>
                                  <Text
                                    fontSize="2xl"
                                    fontWeight="bold"
                                    color="blue.500"
                                  >
                                    {avgScore}%
                                  </Text>
                                  <Text fontSize="sm" color={muted}>
                                    Score
                                  </Text>
                                </VStack>
                                <VStack>
                                  <Text
                                    fontSize="2xl"
                                    fontWeight="bold"
                                    color="green.500"
                                  >
                                    {avgCompletion}%
                                  </Text>
                                  <Text fontSize="sm" color={muted}>
                                    Completion
                                  </Text>
                                </VStack>
                              </SimpleGrid>

                              <HStack justify="space-between" fontSize="sm">
                                <Text>
                                  Top:{' '}
                                  {
                                    classStudents.filter(
                                      (s) =>
                                        s.performance === 'excellent' ||
                                        s.performance === 'good',
                                    ).length
                                  }
                                </Text>
                                <Text color="red.500">
                                  Risk:{' '}
                                  {
                                    classStudents.filter(
                                      (s) =>
                                        s.riskLevel === 'high' ||
                                        s.riskLevel === 'medium',
                                    ).length
                                  }
                                </Text>
                              </HStack>
                            </VStack>
                          </Card>
                        );
                      })}
                    </SimpleGrid>
                  </VStack>
                </Skeleton>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>
    </AdminLayout>
  );
};

// Helper function to fetch students with analytics
const fetchStudentsWithAnalytics = async (
  students: StudentProfile[],
  analyticsService: ReturnType<typeof useAnalyticsService>,
) => {
  return await analyticsService.getStudentsWithAnalytics(students, {
    scopeByStudentClass: true,
  });
};
